import { defineStore, type Store } from "pinia";
import joi from "joi";
import {
  fromUnixToAPIDate,
  getEndDate,
  getStartDate,
  toLongDateRange,
  toUnix,
} from "@/stories/utils/dates";
import { useAuthStore } from "@/stores/auth.module";
import md5 from "md5";
import type {
  IDefaultState,
  IDefaultStoreActions,
  IDefultStoreGetters,
  IFileState,
  IMultiSelectItem,
  IOption,
} from "@/interfaces";
import type {
  StoreName,
  TabErrorSchemaTypes,
  RruleOptions,
  EditRepeatTypes,
  IdType,
  RRuleSupportedEntityType,
  DefaultFilterType,
  MeetingParticipantData,
  MeetingSuggestionType,
  ListResourceType,
} from "@/types";
import helpers from "@/helpers";
import {
  type NotificationSettingsInput,
  type SummarizedMeetingsFragment,
  type MeetingInput,
  type EntityPagerFilter,
  type MeetingAgendaPointInput,
  Profile_Type,
  Sort_Field,
  Sort_Order,
} from "@/graphql/generated-types";
import { fixValidationFields } from "@/helpers/forms/validate";
import { globali18n } from "@/i18n";
import { DEFAULT_NOTIFICATION_SETTINGS, REPEAT_RULE_OPTIONS } from "@/consts";
import toImageList from "@/helpers/files/toImageList";
import toRRule from "@/helpers/rrules/toRRule";
import { RRule, RRuleSet, rrulestr } from "rrule";
import fromRRuletoRuleState from "@/helpers/rrules/fromRRuletoRuleState";
import PagerManager from "@/helpers/manager/PagerManager";
import api from "@/api";
import { useDepartments } from "@/composables/useDepartments";
import converters from "@/converters";
import whenComposablesReady from "@/helpers/requests/whenComposablesReady";
import { useDefaults } from "@/composables/useDefaults";
import getNotificationSettings from "@/services/notications/getNotificationSettings";
import ChangeTracker from "@/helpers/forms/ChangeTracker";
import { sentryException } from "@/sentry";
import { useMeetingAgendaPoints } from "@/composables/useMeetingAgendaPoints";
import getFallbackImage from "@/helpers/files/getFallbackImage";

const VALIDATION_SCHEMA = joi.object({
  title: joi.string().max(50).required(),
  startDate: joi.number().required(),
  endDate: joi.number().required().greater(joi.ref("startDate")),
  image: joi.array().min(1).max(1),
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  coreData: ["title", "endDate"],
  pictures: ["image"],
  pdf: [],
};

export interface IMeetingItem {
  id?: number;
  title: string;
  location: string;
  startDate: number;
  endDate: number;
  originalStartDate?: number;
  originalEndDate?: number;
  image?: string | null | undefined;
  imageList: IFileState[];
  price?: string;
  vacation?: boolean;
  editRepeat: EditRepeatTypes;
  rRule?: RRule | RRuleSet;
  repeatId?: string;
  category?: string;
  departments: string[];
  groups: string[];
  externalGroups: string[];
  parentGroups: string[];
  isCancelled: boolean;
  signupEnabled: boolean;
  participantCount: number;
  __typename: RRuleSupportedEntityType;
  participants: MeetingParticipantData;
  agendaPoints: IMultiSelectItem[];
  suggestions: MeetingSuggestionType[];
}

export interface MeetingsListFilters {
  categories: string[];
  groups: string[];
  departments: string[];
}

export interface IMeetingsState extends IDefaultState {
  listFilters: MeetingsListFilters;
  item: IMeetingItem;
  notification: NotificationSettingsInput;
  pager: PagerManager<SummarizedMeetingsFragment>;
  selectedRruleOption: IOption;
  rruleToSave?: RruleOptions;
  participantTracker: ChangeTracker;
  listResourceType: ListResourceType;
}

export type MeetingsStore = Store<
  string,
  IMeetingsState,
  IDefultStoreGetters<IMeetingsState>,
  IDefaultStoreActions
>;

const getDefaultState = (): IMeetingsState => {
  const item: IMeetingItem = {
    __typename: "SingleMeeting",
    title: "",
    location: "",
    startDate: getStartDate(),
    endDate: getEndDate(),
    image: undefined,
    imageList: [],
    editRepeat: "single",
    category: undefined,
    departments: [],
    groups: [],
    externalGroups: [],
    parentGroups: [],
    isCancelled: false,
    signupEnabled: true,
    participantCount: 0,
    participants: {
      employeesSignedUp: [],
      residentsSignedUp: [],
    },
    agendaPoints: [],
    suggestions: [],
  };

  return {
    listFilters: {
      categories: [],
      groups: [],
      departments: [],
    },
    tabErrors: {},
    originalHash: null,
    validation: null,
    pager: new PagerManager<SummarizedMeetingsFragment>(),
    item,
    notification: {
      ...DEFAULT_NOTIFICATION_SETTINGS,
    },
    selectedRruleOption: REPEAT_RULE_OPTIONS[0]!,
    rruleToSave: undefined,
    participantTracker: new ChangeTracker(item.participants, [
      "employeesSignedUp",
      "residentsSignedUp",
    ]),
    listResourceType: "internal",
  };
};

export const createUseStore = (id: StoreName) => {
  return defineStore(id, {
    state: (): IMeetingsState => getDefaultState(),
    getters: {
      employeeSuggestions(state) {
        return state.item.suggestions.filter(
          (item) =>
            item.profileType.toLowerCase() ===
            Profile_Type.Employee.toLowerCase(),
        );
      },
      residentSuggestions(state) {
        return state.item.suggestions.filter(
          (item) =>
            item.profileType.toLowerCase() ===
            Profile_Type.Resident.toLowerCase(),
        );
      },
    },
    actions: {
      hasUnsavedChanges() {
        return !!(
          this.item &&
          this.originalHash &&
          this.originalHash !== this.currentHash()
        );
      },
      currentHash(): string {
        const data = {
          ...this.getItem(),
          imageList: this.item.imageList,
          notification: this.notification,
          selectedRruleOption: this.selectedRruleOption,
          rruleToSave: this.rruleToSave,
        };
        return md5(JSON.stringify(data));
      },
      setOriginalHash() {
        this.originalHash = this.currentHash();
      },
      async setItem(id: IdType) {
        // No need to refetch
        if (id && this.item.id?.toString() === id.toString()) {
          return;
        }

        const data = await api.meetings.getMeetingDetail(id);
        if (!data) {
          return;
        }

        this.item = {
          ...getDefaultState().item,
          id: parseInt(data.id),
          title: data.title,
          location: data.location || "",
          startDate: toUnix(new Date(data.startDate)),
          endDate: toUnix(new Date(data.endDate)),
          originalStartDate: toUnix(new Date(data.startDate)),
          originalEndDate: toUnix(new Date(data.endDate)),
          imageList: toImageList(data),
          editRepeat: "single" as EditRepeatTypes,
          isCancelled: data.cancelled,
          signupEnabled: data.signupEnabled,
          // This is temporary. They will be set after the whenComposablesReady().
          departments: [],
          groups: [],
          externalGroups: [],
          parentGroups: [],
          participantCount: data.participants?.length || 0,
          __typename: data.__typename as RRuleSupportedEntityType,
          participants: {
            employeesSignedUp: [],
            residentsSignedUp: [],
          },
          agendaPoints: (data.agendaPoints || []).map((agendaPoint) => {
            return {
              id: agendaPoint.id,
              title: agendaPoint.title,
              imageURL: agendaPoint.picture?.source,
              imageID: agendaPoint.picture?.id,
            } as IMultiSelectItem;
          }),
          suggestions: (data.suggestions || []).map((suggestion) => {
            return {
              id: suggestion.id,
              title: suggestion.title,
              status: suggestion.status,
              profileType: suggestion.suggester?.__typename as Profile_Type,
              profileName: suggestion.suggester?.displayName || "",
              profileImageURL: suggestion.suggester?.image?.source,
            } as MeetingSuggestionType;
          }),
        };

        if (this.item.imageList.length === 0) {
          this.item.imageList.push(await getFallbackImage("meeting"));
        }

        // We should wait the list to be loaded to select departments. Because API
        // returns departments and external departments in the same array. We don't
        // know which id is related with which list. That causes a race condition
        // issue.
        const departments = useDepartments();

        // We don't know when the composable data will be loaded. But we can not
        // wait to select an item because it effects lots of things such as
        // spinners, warnings, etc. Because of the race condition issue, setting
        // the following data should be wait a little. This will be occurred only
        // when the user refresh the page on meetings. Once the data is ready,
        // users can't notice any difference.
        whenComposablesReady([departments], () => {
          this.item.departments = converters.shared.toSelectableIds(
            departments.departmentList,
            data.departments,
          );

          this.setOriginalHash();
        });

        // Setting the rrule data
        if (data.__typename === "RepeatingMeeting" && data.repeat) {
          const rule = rrulestr(data.repeat.rrule);
          this.rruleToSave = fromRRuletoRuleState(rule);
          this.item.rRule = rule;
          this.item.repeatId = data.repeat.id;
        }

        this.item.category = data.meetingCategory?.id || undefined;

        this.item.signupEnabled = data.signupEnabled;

        // Setting participants
        this.item.participants = converters.meeting.toParticipantsData(data);

        const { getMeetingNotificationSettings } = useDefaults();
        this.notification = getNotificationSettings(
          data,
          this.item,
          getMeetingNotificationSettings,
        );

        // We need to track those changes on participants
        this.participantTracker = new ChangeTracker(this.item.participants, [
          "employeesSignedUp",
          "residentsSignedUp",
        ]);

        this.setOriginalHash();
        this.validation = null;
      },
      getItem(): MeetingInput {
        const authStore = useAuthStore();
        const item = { ...this.item };

        const getSuggestionsByType = (type: Profile_Type) => {
          return this.item.suggestions
            .filter(
              (item) => item.profileType.toLowerCase() === type.toLowerCase(),
            )
            .map((item) => {
              return {
                id: item.id,
                status: item.status,
              };
            });
        };

        const id = item?.id?.toString();

        let rruleString;
        let isRepeat = false;
        let repeat;

        // Setting the repeat rule for repeating items
        if (this.rruleToSave) {
          this.rruleToSave.startDate = this.item.startDate;
          const rule = toRRule(this.rruleToSave);
          rruleString = rule.toString();
          isRepeat = true;
          repeat = item.repeatId;
        }

        // We shouldn't marked as repeat on updates if it's not selected a repeat
        if (id && this.item.editRepeat === "single") {
          isRepeat = false;
          repeat = null;
          rruleString = null;
        }

        const { departments } = converters.shared.toSharedOptions(item);

        const input: MeetingInput = {
          id,
          institution: authStore.institutionId,
          title: item.title,
          isRepeat,
          rrule: rruleString,
          repeat,
          signup: item.signupEnabled,
          status: item.isCancelled ? 2 : 1,
          startDate: fromUnixToAPIDate(item.startDate),
          endDate: fromUnixToAPIDate(item.endDate),
          location: item.location,
          // We have to send NULL to make the field empty. Undefined doesn't work
          category: item.category || null,
          departments,
          suggestionsByEmployees: getSuggestionsByType(Profile_Type.Employee),
          suggestionsByResidents: getSuggestionsByType(Profile_Type.Resident),
        };

        // We should only save those values if they're changed
        this.participantTracker.appendIfChanged(input, "employeesSignedUp");
        this.participantTracker.appendIfChanged(input, "residentsSignedUp");

        // Setting agenda points
        const { agendaPoints } = useMeetingAgendaPoints();
        input.agendaPoints = this.item.agendaPoints.map((item) => {
          const agendaPoint = agendaPoints.value.find((i) => i.id === item.id);
          return {
            title: agendaPoint?.title || item.title,
            picture: item.imageID || agendaPoint?.picture?.id,
          } as MeetingAgendaPointInput;
        });

        return input;
      },
      async hasStaleData() {
        // If there is no change, we shouldn't check stale data for participants
        if (this.participantTracker.hasAnyChange() !== true) {
          return false;
        }

        const freshData = await api.meetings.getMeetingDetail(this.item.id);
        if (!freshData) {
          sentryException(new Error("The selected item not found"));
          return;
        }

        // Let's check if the initial version were different than the fresh version
        return this.participantTracker.isStale(
          converters.meeting.toParticipantsData(freshData),
        );
      },
      async isNotValid() {
        this.validation = helpers.forms.validate(VALIDATION_SCHEMA, {
          ...this.item,
          image: this.item.imageList.filter((item) => item.action !== "delete"),
        });

        // We can't use usei18n() in stores.
        // https://stackoverflow.com/a/71449708
        const { t } = globali18n;

        // We should fix the validation labels
        fixValidationFields(this.validation, {
          startDate: t("global.start"),
        });

        this.tabErrors = helpers.forms.getTabErrors(
          TAB_FIELDS_SCHEMA,
          this.validation.errors,
        );

        return !this.validation.isValid;
      },
      async fetchItems(filter: DefaultFilterType) {
        const { dateFrom, dateTo } = toLongDateRange(
          filter === "show_upcoming",
        );

        const params: EntityPagerFilter = {
          dateFrom,
          dateTo,
          sorting: {
            field: Sort_Field.StartDate,
            order:
              filter === "show_upcoming" ? Sort_Order.Asc : Sort_Order.Desc,
          },
        };
        await this.pager.paginate(api.meetings.getMeetings, params, this);
      },
    },
  });
};

// View store
export const useMeetingsStore = createUseStore("MEETINGS");

// We use the same store structure for the create modal.
export const useAddMeetingStore = createUseStore("ADD_MEETINGS");
